import { useEffect, useId } from 'react';
import Modal from 'react-bootstrap/Modal';
import { KTIcon } from '@/_metronic/helpers';
import { type AnnouncementRecipientDto } from '@/app/models/AnnouncementRecipientDto';
import AnnouncementAcknowledgement from '@/app/modules/announcements/components/AnnouncementAcknowledgement';
import AnnouncementContent from '@/app/modules/announcements/components/AnnouncementContent';
import AnnouncementModalBand from '@/app/modules/announcements/components/AnnouncementModalBand';
import {
  useAcknowledgeAnnouncement,
  useDismissAnnouncement,
  useMarkAnnouncementSeen,
  usePendingAnnouncements,
} from '@/app/modules/announcements/core/_hooks';
import { isMandatory, publishedLabel } from '@/app/modules/announcements/core/_utils';
import '@/app/modules/announcements/components/AnnouncementModal.scss';

type AnnouncementModalProps = {
  announcement: AnnouncementRecipientDto;
  onClose: () => void;
};

const AnnouncementModal = ({ announcement, onClose }: AnnouncementModalProps) => {
  const titleId = useId();
  const { data: pending = [] } = usePendingAnnouncements();
  const { mutate: markSeen } = useMarkAnnouncementSeen();
  const acknowledge = useAcknowledgeAnnouncement();
  const dismiss = useDismissAnnouncement();

  // O backend já decide o que ainda está pendente; o front só lê a lista, sem contar vigência.
  const stillPending = pending.some((item) => item.id === announcement.id);
  const mandatory = isMandatory(announcement.type.value);
  const blocking = mandatory && (stillPending || acknowledge.isPending);
  const dismissible = !mandatory && (stillPending || dismiss.isPending || dismiss.isSuccess);
  const saveFailed = acknowledge.isError || dismiss.isError;
  const senderLabel = publishedLabel(announcement.published_by_user_name, announcement.published_date);

  useEffect(() => {
    if (announcement.seen_date) return;
    markSeen(announcement.id);
  }, [announcement.id, announcement.seen_date, markSeen]);

  const handleAcknowledge = () => acknowledge.mutate(announcement.id, { onSuccess: onClose });

  const handleClose = () => {
    // Fundo e Esc também chamam o fechar; sem isso a dispensa sairia duas vezes enquanto salva.
    if (dismiss.isPending) return;
    if (!dismissible) {
      onClose();
      return;
    }
    dismiss.mutate(announcement.id, { onSuccess: onClose });
  };

  const errorAlert = saveFailed ? (
    <div className="alert alert-danger text-start mb-6" role="alert">
      Não foi possível salvar. Tente de novo.
    </div>
  ) : null;

  const dismissOrCloseButton = dismissible ? (
    <button
      className="btn announcement-modal-confirm announcement-modal-action w-100 d-flex align-items-center justify-content-center gap-2"
      disabled={dismiss.isPending}
      type="button"
      onClick={handleClose}
    >
      <KTIcon className="fs-2" iconName="check" />
      Entendi
    </button>
  ) : (
    <button className="btn btn-light announcement-modal-action w-100" type="button" onClick={handleClose}>
      Fechar
    </button>
  );

  const actions = blocking ? (
    <AnnouncementAcknowledgement acknowledging={acknowledge.isPending} onAcknowledge={handleAcknowledge} />
  ) : (
    dismissOrCloseButton
  );

  return (
    <Modal
      centered
      show
      aria-labelledby={titleId}
      backdrop={blocking ? 'static' : true}
      dialogClassName="announcement-modal"
      keyboard={!blocking}
      onHide={handleClose}
    >
      <AnnouncementModalBand closable={!blocking} closing={dismiss.isPending} onClose={handleClose} />
      <AnnouncementContent
        body={announcement.body}
        senderLabel={senderLabel}
        title={announcement.title}
        titleId={titleId}
      >
        {errorAlert}
        {actions}
      </AnnouncementContent>
    </Modal>
  );
};

export default AnnouncementModal;
